import React, { useEffect, useRef, useState } from 'react';
import { T } from './activityStepper.config';

/**
 * The case picker: a DROPDOWN you type into.
 *
 * Adam, 10 Oct: "this case should be, you know, a dropdown", and "this should
 * search against all cases." Both halves matter — the control carries the
 * chevron its neighbours carry, the menu opens on click or focus, and what you
 * type filters the list in place.
 *
 * Mirrors scale-portal's CaseSelect exactly — same behaviour, same wording,
 * same empty-state rule — so the two products agree.
 *
 * WHY THE SEARCH IS IN THE CONTROL, NOT IN THE MENU
 * --------------------------------------------------
 * The first version was a closed select whose MENU held a search box, which
 * opened as two stacked boxes — a field reading "No case" and, below it, an
 * identically sized field reading "Search by case name or number". Two controls
 * for one decision. Typing happens in the control itself now.
 *
 * "NO CASE" IS THE EMPTY STATE
 * ----------------------------
 * There is no "No case" option, because an empty field already is one. That is
 * also why the box is CLEARED when it closes with nothing picked: a field left
 * reading "Smith" that is about to save as no case would be lying about what it
 * holds. Either it shows a case that is really attached, or it is empty.
 */
const CaseSelect = ({
  value,              // the chosen case row, or null for "No case"
  options,            // [{ id, case_name, case_number }]
  query,
  onQueryChange,
  onChange,
  loading = false,
  disabled = false,
  placeholder = 'Search by case name or number',
}) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const labelOf = (row) =>
    row ? (row.case_number ? `${row.case_name} · ${row.case_number}` : row.case_name) : '';

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current?.contains(e.target)) return;
      setOpen(false);
      /* Nothing was chosen, so the typed text is not a case — drop it rather
       * than leave the field showing something it will not save. */
      if (!value) onQueryChange('');
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, value, onQueryChange]);

  const pick = (row) => {
    onChange(row);
    onQueryChange('');
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    onQueryChange('');
    setOpen(false);
  };

  /* A chosen case WINS over the query box, so the field reads as the case once
   * one is attached. Typing then replaces it — see onChange below. */
  const text = value ? labelOf(value) : query;

  return (
    <div className="relative" ref={boxRef}>
      {/* INLINE STYLES, matching the mediator's portal exactly: 44px tall,
          10px radius, 15px text, a briefcase at the left and a clear at the
          right with no chevron. Several of these have no Tailwind class in
          this build to reach for, and the point is that the two portals look
          the same. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 44,
          padding: '0 8px 0 12px',
          borderRadius: 10,
          border: `1px solid ${open ? T.green : T.border}`,
          background: T.white,
          boxSizing: 'border-box',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#5B6663"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flex: 'none' }}
        >
          <rect x="3" y="7" width="18" height="13" rx="2" />
          <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
        </svg>

        <input
          type="text"
          value={text}
          disabled={disabled}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => {
            /* Editing the text means the attached case no longer matches what
             * is on screen, so the selection goes and the case manager is
             * searching again. Without this the field would show one case
             * while holding another. */
            if (value) onChange(null);
            onQueryChange(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              /* The MENU closes, not the dialog behind it. */
              e.stopPropagation();
              setOpen(false);
              if (!value) onQueryChange('');
            }
          }}
          style={{
            flex: 1,
            minWidth: 0,
            height: 40,
            border: 0,
            outline: 'none',
            background: 'transparent',
            font: 'inherit',
            fontSize: 15,
            color: T.ink,
          }}
        />

        {/* Clear sits INSIDE the field, so the control stays one box. Shown
            only once a case is attached — while the field is just a search
            there is nothing to clear back to. */}
        {value && !disabled && (
          <button
            type="button"
            aria-label="Clear case"
            title="Clear case"
            onClick={clear}
            style={{
              flex: 'none',
              width: 28,
              height: 28,
              border: 0,
              background: 'none',
              borderRadius: 8,
              display: 'grid',
              placeItems: 'center',
              color: T.faint,
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}
      </div>

      {/* Open whenever the control is active and no case is attached — this is
          a dropdown, so clicking it shows the list even before anything is
          typed. With a case attached there is nothing to choose. */}
      {open && !value && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 'calc(100% + 6px)',
            zIndex: 10003,
            background: T.white,
            border: `1px solid ${T.borderMenu}`,
            borderRadius: 14,
            boxShadow: '0 10px 30px rgba(16,32,28,.13)',
            padding: 6,
            boxSizing: 'border-box',
          }}
        >
          <div style={{ maxHeight: 248, overflowY: 'auto' }}>
            {/* Only when there is nothing to show — with results on screen the
                list stays put and updates in place. */}
            {loading && options.length === 0 && (
              <p style={{ padding: '10px', fontSize: 15, color: T.faint, margin: 0 }}>Searching…</p>
            )}

            {/* One character is held back by the caller — it matches most of
                the list and the result is noise. */}
            {!loading && query.trim().length === 1 && (
              <p style={{ padding: '10px', fontSize: 15, color: T.faint, margin: 0 }}>Keep typing to search</p>
            )}

            {!loading && query.trim().length !== 1 && options.length === 0 && (
              <p style={{ padding: '10px', fontSize: 15, color: T.faint, margin: 0 }}>
                {query.trim() ? `No cases match “${query.trim()}”` : 'No cases yet'}
              </p>
            )}

            {/* With an empty box these are the mediator's most recent cases,
                newest first; once something is typed they are matches from
                every case they own. */}
            {!loading &&
              query.trim().length !== 1 &&
              options.map((c) => (
                <Row
                  key={c.id}
                  label={labelOf(c)}
                  selected={value?.id === c.id}
                  onClick={() => pick(c)}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

/** One result row — label left, the product's green check right. */
const Row = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      height: 40,
      padding: '0 10px',
      borderRadius: 8,
      border: 0,
      textAlign: 'left',
      font: 'inherit',
      fontSize: 15,
      cursor: 'pointer',
      background: selected ? T.greenWash : T.white,
      color: selected ? T.greenDark : T.inkSoft,
      fontWeight: selected ? 600 : 400,
    }}
  >
    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label}
    </span>
    {selected && <span style={{ flex: 'none', color: T.green, fontSize: 13 }}>✓</span>}
  </button>
);

export default CaseSelect;
